import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowForward,
  ArrowBack,
  SaveOutlined,
  PlayArrow as ResumeIcon,
  AddCircleOutline,
  DeleteOutline,
  Assignment,
  Language as LanguageIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { clientPlanService } from '../services/clientPlanService';
import { cartService } from '../services/cartService';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ClientPlan } from '../types/planning';

function siteInitials(url: string): string {
  const host = (url || '').replace(/^https?:\/\//, '').replace(/^www\./, '');
  return host.slice(0, 2).toUpperCase();
}

function formatSavedDate(value?: string): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

interface SavedPlanCardProps {
  plan: ClientPlan;
  active: boolean;
  busy: boolean;
  activePanel?: React.ReactNode;
  onToggle: () => void;
  onAddSites: () => void;
  onDelete: () => void;
}

interface PlanSiteRow {
  guestBlogSite?: { id?: string; site_url?: string; da?: number; dr?: number; base_price?: number };
  guestBlogSiteId?: string;
}

const SavedPlanCard: React.FC<SavedPlanCardProps> = ({
  plan,
  active,
  busy,
  activePanel,
  onToggle,
  onAddSites,
  onDelete,
}) => {
  const { data: sitesResp } = useQuery({
    queryKey: ['plan-sites', plan.id],
    queryFn: () => clientPlanService.getPlanSites(plan.id),
    staleTime: 30 * 1000,
  });
  const sites = (sitesResp?.data || []) as PlanSiteRow[];
  const total = sites.reduce((t, s) => t + (Number(s.guestBlogSite?.base_price) || 0), 0);
  const count = sitesResp ? sites.length : (plan.siteCount ?? 0);

  return (
    <div className={`wf-step-saved-card${active ? ' active' : ''}`}>
      <div
        className="wf-step-saved-card-head"
        style={{ cursor: busy ? 'wait' : 'pointer' }}
        onClick={() => !busy && onToggle()}
      >
        <span className="wf-step-saved-card-icon" title={active ? 'Close order' : 'Open order'}>
          {busy ? (
            <ResumeIcon sx={{ fontSize: 18 }} />
          ) : active ? (
            <ExpandLess sx={{ fontSize: 20 }} />
          ) : (
            <ExpandMore sx={{ fontSize: 20 }} />
          )}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#2563EB',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={plan.planName}
            >
              {plan.planName}
            </div>
            {active && (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  padding: '2px 7px',
                  borderRadius: 5,
                  background: '#2563EB',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                Open
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ background: '#F1F5F9', borderRadius: 5, padding: '1px 7px' }}>
              {count} site{count !== 1 ? 's' : ''}
            </span>
            {total > 0 && <span style={{ color: '#2563EB', fontWeight: 600 }}>${total}</span>}
            <span>Saved {formatSavedDate(plan.updatedAt || plan.createdAt)}</span>
          </div>
        </div>
        {!active && (
          <button
            type="button"
            disabled={busy}
            className="wf-step-btn-outline"
            style={{ fontSize: 11, padding: '6px 12px', background: '#EEF2FF', color: '#2563EB', borderColor: '#C7D2FE' }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <ResumeIcon sx={{ fontSize: 14 }} />
            Select order
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          className="wf-step-btn-outline"
          style={{ fontSize: 11, padding: '6px 12px' }}
          onClick={(e) => {
            e.stopPropagation();
            onAddSites();
          }}
        >
          <AddCircleOutline sx={{ fontSize: 14 }} />
          Add sites
        </button>
        <button
          type="button"
          className="wf-step-remove-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete plan"
        >
          <DeleteOutline sx={{ fontSize: 18 }} />
        </button>
      </div>

      {active && activePanel && <div className="wf-step-saved-card-body">{activePanel}</div>}
    </div>
  );
};

const ProjectPlanWorkflowPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProject, goToStep, markStepDone } = useProjectWorkflow();
  const { user } = useAuth();

  const clientId = searchParams.get('clientId') || selectedProject?.clientId || user?.clientId || '';
  const projectId = searchParams.get('projectId') || selectedProject?.id || '';
  const urlPlanId = searchParams.get('planId') || '';

  const { cart, removeFromCart, clearCart, refreshCart, cartTotal, loading } = useCart(
    projectId || undefined
  );
  const cartItems = cart?.cartItems || [];

  const currentSelectionRef = React.useRef<HTMLDivElement>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [planName, setPlanName] = useState('');
  const currentPlanId = urlPlanId;
  const [resumingPlanId, setResumingPlanId] = useState<string | null>(null);
  const [planLoadError, setPlanLoadError] = useState<string | null>(null);

  const planQuery = (id?: string) => {
    const base = `projectId=${encodeURIComponent(projectId)}&clientId=${encodeURIComponent(clientId)}`;
    return id ? `${base}&planId=${encodeURIComponent(id)}` : base;
  };

  const { data: plansResp, isLoading: plansLoading } = useQuery({
    queryKey: ['wf-saved-plans', clientId, projectId],
    queryFn: () =>
      clientPlanService.getPlans({
        clientId,
        projectId: projectId || undefined,
        status: 'ACTIVE',
        planType: 'ORDER_DRAFT',
      }),
    enabled: Boolean(clientId) && Boolean(projectId),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
  const savedPlans = (plansResp?.data || []) as ClientPlan[];

  const saveMutation = useMutation({
    mutationFn: async (name?: string) => {
      const siteIds = cartItems.map((i) => i.siteId).filter(Boolean);
      if (currentPlanId) {
        if (siteIds.length > 0) {
          await clientPlanService.addSitesToPlan(currentPlanId, siteIds);
        }
        return { id: currentPlanId };
      }
      const plan = await clientPlanService.createPlan({
        planName: (name || '').trim(),
        clientId,
        projectId: projectId || undefined,
        planType: 'ORDER_DRAFT',
        description: selectedProject?.projectName
          ? `Project: ${selectedProject.projectName}`
          : undefined,
      });
      if (siteIds.length > 0) {
        await clientPlanService.addSitesToPlan(plan.id, siteIds);
      }
      return plan;
    },
    onSuccess: async (plan) => {
      setSaveDialogOpen(false);
      setPlanName('');
      markStepDone('plan');
      await queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
      await queryClient.invalidateQueries({ queryKey: ['plan-sites', plan.id] });
      navigate(`/project-workflow/plan?${planQuery(plan.id)}`, { replace: true });
      requestAnimationFrame(() => {
        currentSelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientPlanService.deletePlan(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
      if (id === currentPlanId) {
        navigate(`/project-workflow/plan?${planQuery()}`, { replace: true });
      }
    },
  });

  const { data: activePlanSitesResp } = useQuery({
    queryKey: ['plan-sites', currentPlanId],
    queryFn: () => clientPlanService.getPlanSites(currentPlanId),
    enabled: Boolean(currentPlanId),
    staleTime: 30 * 1000,
  });

  const loadPlanIntoSelection = async (plan: ClientPlan) => {
    setResumingPlanId(plan.id);
    setPlanLoadError(null);
    try {
      const resp = await clientPlanService.getPlanSites(plan.id);
      const sites = (resp.data || []) as Array<{
        guestBlogSiteId?: string;
        guestBlogSite?: { id?: string; base_price?: number; status?: string };
      }>;

      const existing = cart?.cartItems || [];
      await Promise.all(existing.map((it) => cartService.removeFromCart(it.id)));

      let addedCount = 0;
      const errors: string[] = [];

      for (const s of sites) {
        const siteId = s.guestBlogSite?.id || s.guestBlogSiteId;
        if (!siteId) continue;
        if (s.guestBlogSite?.status && s.guestBlogSite.status !== 'ACTIVE') {
          errors.push('One or more sites are no longer active');
          continue;
        }
        try {
          await cartService.addToCart({
            projectId,
            clientId,
            siteId,
            submissionType: 'GBS',
            price: Number(s.guestBlogSite?.base_price) || 0,
          });
          addedCount += 1;
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Could not add site to cart';
          if (!errors.includes(msg)) errors.push(msg);
        }
      }

      await refreshCart();

      if (addedCount === 0 && sites.length > 0) {
        const message =
          errors[0] ||
          'This saved order can no longer be opened. It may already have been placed or the sites are unavailable.';
        setPlanLoadError(message);
        await clientPlanService.fulfillSavedOrder(plan.id);
        await queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
        if (currentPlanId === plan.id) {
          navigate(`/project-workflow/plan?${planQuery()}`, { replace: true });
        }
        return;
      }

      if (errors.length > 0 && addedCount > 0) {
        setPlanLoadError(`Some sites could not be loaded: ${errors.join('; ')}`);
      }

      markStepDone('plan');
      navigate(`/project-workflow/plan?${planQuery(plan.id)}`, { replace: true });
      requestAnimationFrame(() => {
        currentSelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } finally {
      setResumingPlanId(null);
    }
  };

  const handleResume = (plan: ClientPlan) => {
    void loadPlanIntoSelection(plan);
  };

  const handleCollapsePlan = async () => {
    navigate(`/project-workflow/plan?${planQuery()}`, { replace: true });
    await clearCart();
    await refreshCart();
  };

  const autoOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpenedRef.current) return;
    if (plansLoading || resumingPlanId || !cart) return;
    if (currentPlanId || cartItems.length > 0) {
      autoOpenedRef.current = true;
      return;
    }
    if (savedPlans.length > 0) {
      autoOpenedRef.current = true;
      void loadPlanIntoSelection(savedPlans[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoading, resumingPlanId, currentPlanId, cartItems.length, savedPlans, cart]);

  const handleAddSitesToPlan = async (plan: ClientPlan) => {
    await loadPlanIntoSelection(plan);
    navigate(`/sites?${planQuery(plan.id)}`);
  };

  const handleStartNewPlan = async () => {
    if (cart && cart.cartItems.length > 0) {
      await clearCart();
      await refreshCart();
    }
    navigate(`/sites?${planQuery()}`);
  };

  const handleSaveAndExit = () => {
    if (cartItems.length === 0) return;
    if (currentPlanId) {
      saveMutation.mutate(undefined);
    } else {
      setSaveDialogOpen(true);
    }
  };

  const handleRemoveSite = async (cartItemId: string, siteId?: string) => {
    await removeFromCart(cartItemId);
    if (currentPlanId && siteId) {
      try {
        await clientPlanService.removeSiteFromPlan(currentPlanId, siteId);
        queryClient.invalidateQueries({ queryKey: ['plan-sites', currentPlanId] });
        queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
      } catch {
        /* selection already updated */
      }
    }
  };

  const hasUnsavedChanges = React.useMemo(() => {
    const currentIds = new Set(cartItems.map((ci) => ci.siteId));
    if (currentIds.size === 0) return false;
    if (!currentPlanId) return true;
    const savedSites = (activePlanSitesResp?.data || []) as Array<{
      guestBlogSiteId?: string;
      guestBlogSite?: { id?: string };
    }>;
    const savedIds = new Set(
      savedSites.map((s) => s.guestBlogSite?.id || s.guestBlogSiteId).filter(Boolean) as string[]
    );
    for (const id of Array.from(currentIds)) {
      if (!savedIds.has(id)) return true;
    }
    for (const id of Array.from(savedIds)) {
      if (!currentIds.has(id)) return true;
    }
    return false;
  }, [cartItems, currentPlanId, activePlanSitesResp]);

  if (!projectId) {
    return <p style={{ color: '#64748B' }}>Select a project to build a plan.</p>;
  }

  if (projectId && !cart && loading && !resumingPlanId && !saveMutation.isPending) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner />
      </div>
    );
  }

  const hasSelection = cartItems.length > 0;
  const activeSavedPlan = savedPlans.find((p) => p.id === currentPlanId);
  const showSaveExit = hasSelection && hasUnsavedChanges && !currentPlanId;

  const selectionSection = !hasSelection ? (
    <div className="wf-step-empty">
      <Assignment sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
      <p className="wf-step-empty-title">No sites selected yet</p>
      <p style={{ marginBottom: 20 }}>
        Resume a saved order above, or go to Explore Sites to add new ones.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          className="wf-btn-create"
          onClick={() => void handleStartNewPlan()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', fontSize: 14 }}
        >
          <LanguageIcon sx={{ fontSize: 18 }} />
          Explore sites
        </button>
      </div>
    </div>
  ) : (
    <>
      <div className="wf-step-panel-head">
        <div>
          {activeSavedPlan && (
            <div className="wf-step-panel-title">
              Order: <span className="accent">{activeSavedPlan.planName}</span>
            </div>
          )}
          <div className="wf-step-panel-hint">
            {activeSavedPlan
              ? 'Sites from this saved order. Add or remove below — changes save automatically.'
              : 'Unsaved selection from Explore Sites. Save & exit to keep as a named order.'}
          </div>
        </div>
        <div className="wf-step-actions">
          <button
            type="button"
            className="wf-step-btn-outline"
            onClick={() => {
              if (currentPlanId) {
                navigate(`/sites?${planQuery(currentPlanId)}`);
              } else {
                goToStep('sites');
              }
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add more sites
          </button>
          <span className="wf-step-badge">
            {cartItems.length} site{cartItems.length !== 1 ? 's' : ''} selected
          </span>
        </div>
      </div>

      <div className="sites-table-container">
        <table className="sites-table">
          <thead>
            <tr>
              <th>Site</th>
              <th style={{ width: 70 }}>DA</th>
              <th style={{ width: 70 }}>DR</th>
              <th style={{ width: 90 }}>Price</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {cartItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="wf-step-site-avatar">{siteInitials(item.siteUrl)}</span>
                    <div>
                      <div className="wf-step-site-url">{item.siteUrl}</div>
                      <div className="wf-step-site-type">Guest post</div>
                    </div>
                  </div>
                </td>
                <td className="wf-step-metric-da">{item.siteDa}</td>
                <td className="wf-step-metric-dr">{item.siteDr}</td>
                <td className="wf-step-price">${item.price}</td>
                <td>
                  <button
                    type="button"
                    className="wf-step-remove-btn"
                    onClick={() => void handleRemoveSite(item.id, item.siteId)}
                    title="Remove from plan"
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </td>
              </tr>
            ))}
            <tr className="wf-step-total-row">
              <td colSpan={3} style={{ fontWeight: 600, color: '#1E293B' }}>
                Total
              </td>
              <td className="wf-step-price">${cartTotal}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="wf-step-actions" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="wf-btn-create"
          onClick={() => {
            markStepDone('plan');
            goToStep('order');
          }}
        >
          {currentPlanId ? 'Place order' : 'Save & place order'}
          <ArrowForward sx={{ fontSize: 14 }} />
        </button>
        {showSaveExit && (
          <button
            type="button"
            className="wf-step-btn-outline"
            disabled={saveMutation.isPending}
            onClick={handleSaveAndExit}
          >
            <SaveOutlined sx={{ fontSize: 15 }} />
            {saveMutation.isPending && currentPlanId ? 'Saving…' : 'Save & exit'}
          </button>
        )}
        <button
          type="button"
          className="wf-step-btn-outline"
          onClick={() => {
            if (currentPlanId) {
              navigate(`/sites?${planQuery(currentPlanId)}`);
            } else {
              goToStep('sites');
            }
          }}
        >
          <ArrowBack sx={{ fontSize: 14 }} />
          Back to sites
        </button>
      </div>
    </>
  );

  return (
    <div className="wf-step-page">
      <div className="wf-step-header">
        <div>
          <h1 className="wf-step-title">Create order</h1>
          <p className="wf-step-subtitle">Select a saved order or start fresh by exploring sites.</p>
        </div>
      </div>

      {planLoadError && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 8,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#B91C1C',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {planLoadError}
        </div>
      )}

      {savedPlans.length > 0 && !currentPlanId && hasSelection && (
        <div>
          <div ref={currentSelectionRef} className="wf-step-section-label" style={{ scrollMarginTop: 16 }}>
            Current selection
          </div>
          {selectionSection}
        </div>
      )}

      {(plansLoading || savedPlans.length > 0) && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="wf-step-section-label" style={{ marginBottom: 0 }}>
              Saved orders
            </span>
            <button type="button" className="wf-step-btn-text" onClick={handleStartNewPlan}>
              <AddIcon sx={{ fontSize: 15 }} />
              Start new order
            </button>
          </div>

          {plansLoading ? (
            <div style={{ padding: 16, color: '#64748B', fontSize: 13 }}>Loading plans…</div>
          ) : savedPlans.length === 0 ? (
            <div
              className="wf-step-card wf-step-card-inner"
              style={{ borderStyle: 'dashed', color: '#64748B', fontSize: 13 }}
            >
              No saved orders yet. Build a selection below and click “Save &amp; exit” to keep it.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {savedPlans.map((plan) => (
                <SavedPlanCard
                  key={plan.id}
                  plan={plan}
                  active={plan.id === currentPlanId}
                  busy={resumingPlanId === plan.id}
                  activePanel={
                    plan.id === currentPlanId && hasSelection ? (
                      <div ref={currentSelectionRef} style={{ scrollMarginTop: 16 }}>
                        {selectionSection}
                      </div>
                    ) : undefined
                  }
                  onToggle={() => {
                    if (plan.id === currentPlanId) {
                      void handleCollapsePlan();
                    } else {
                      handleResume(plan);
                    }
                  }}
                  onAddSites={() => handleAddSitesToPlan(plan)}
                  onDelete={() => {
                    if (window.confirm(`Delete plan “${plan.planName}”?`)) deleteMutation.mutate(plan.id);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {!plansLoading && savedPlans.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            className="wf-btn-create"
            onClick={() => void handleStartNewPlan()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 26px', fontSize: 14 }}
          >
            <LanguageIcon sx={{ fontSize: 18 }} />
            Explore sites
          </button>
        </div>
      )}

      {!plansLoading && savedPlans.length === 0 && (
        <div>
          <div ref={currentSelectionRef} className="wf-step-section-label" style={{ scrollMarginTop: 16 }}>
            Current selection
          </div>
          {selectionSection}
        </div>
      )}

      {saveDialogOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={() => !saveMutation.isPending && setSaveDialogOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="wf-step-card"
            style={{ padding: 22, width: 380, maxWidth: '90vw', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
          >
            <div className="wf-step-title" style={{ fontSize: 18, marginBottom: 4 }}>
              Save plan
            </div>
            <div className="wf-step-subtitle" style={{ marginBottom: 14 }}>
              Save this selection of {cartItems.length} site{cartItems.length !== 1 ? 's' : ''} so you can resume it
              later.
            </div>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 6, color: '#475569' }}>
              Plan name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              autoFocus
              className="wf-step-input"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. Q3 Tech Outreach"
              style={{ marginBottom: 8 }}
            />
            {saveMutation.isError && (
              <div className="wf-step-field-error" style={{ marginBottom: 8 }}>
                Failed to save the plan. Please try again.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="wf-step-btn-outline"
                disabled={saveMutation.isPending}
                onClick={() => setSaveDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wf-btn-create"
                disabled={!planName.trim() || saveMutation.isPending}
                onClick={() => saveMutation.mutate(planName.trim())}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save & exit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPlanWorkflowPage;
